/*
 * "What to make next" (2026-07-29 — whitespace recommender, roadmap #4).
 *
 * Pure computation over one product's coverage snapshot: find the
 * angle × avatar × awareness cells where live payback is WELL ABOVE the
 * cold-traffic account average but production is thin or has gone quiet —
 * the whitespace worth briefing into next.
 *
 * Deliberate choices:
 *   - COLD ONLY: retargeting rows are excluded — recommending "make more
 *     retargeting" from warm-audience payback would be circular.
 *   - Proven cells only: a cell must carry ≥ $150 window spend (the same
 *     thin-spend floor the explorer fades) and ≥ 1.15× the account average —
 *     the recommender never extrapolates from noise.
 *   - Low production wins ties: score = vs × log10(spend) ÷ (1 + produced),
 *     so a 1.4× cell with 2 ads made outranks a 1.5× cell with 30.
 */
import type { CoverageProduct } from "./types";

export interface WhitespaceSuggestion {
  angle: string;
  avatar: string;
  awareness: string;
  /** Window spend in the cell (product currency units). */
  spend: number;
  /** Payback vs the cold account average (1.0 = average). */
  vs: number;
  /** Master-sheet rows produced for the cell. */
  produced: number;
  /** Latest WK number seen in the cell (live or produced), or null. */
  latestWeek: number | null;
  /** Operator-facing one-liner (why this cell). */
  reason: string;
}

/** Same thin-spend floor as the explorer. */
const MIN_CELL_SPEND = 150;
/** A cell must beat the account average by this much to be recommended. */
const MIN_VS = 1.15;
/** Weeks behind the product's newest launch that counts as "gone quiet". */
const STALE_WEEKS = 2;
const TOP_N = 5;

function weekNum(label: string | undefined): number | null {
  if (!label) return null;
  const m = /(\d+)/.exec(label);
  return m ? Number(m[1]) : null;
}

interface CellAcc {
  angle: string;
  avatar: string;
  awareness: string;
  sp: number;
  val: number;
  produced: number;
  latestWeek: number | null;
}

export function whatToMakeNext(p: CoverageProduct): WhitespaceSuggestion[] {
  const baseRoas = p.base.roas;
  if (!baseRoas || baseRoas <= 0) return [];

  const cells = new Map<string, CellAcc>();
  let globalLatestWeek: number | null = null;

  const touch = (angle: string, avatar: string, awareness: string): CellAcc => {
    const key = `${angle}|${avatar}|${awareness}`.toLowerCase();
    let c = cells.get(key);
    if (!c) {
      c = { angle, avatar, awareness, sp: 0, val: 0, produced: 0, latestWeek: null };
      cells.set(key, c);
    }
    return c;
  };
  const seeWeek = (c: CellAcc, wk: number | null): void => {
    if (wk === null) return;
    if (c.latestWeek === null || wk > c.latestWeek) c.latestWeek = wk;
    if (globalLatestWeek === null || wk > globalLatestWeek) globalLatestWeek = wk;
  };

  for (const r of p.live) {
    if (r.rt) continue; // cold only
    const { angle, avatar, awareness, week } = r.d;
    if (!angle || !avatar || !awareness) continue;
    const c = touch(angle, avatar, awareness);
    c.sp += r.sp;
    c.val += r.val;
    seeWeek(c, weekNum(week));
  }
  for (const r of p.sheet) {
    const { angle, avatar, awareness, week } = r.d;
    if (!angle || !avatar || !awareness) continue;
    const c = touch(angle, avatar, awareness);
    c.produced += 1;
    seeWeek(c, weekNum(week));
  }

  const out: WhitespaceSuggestion[] = [];
  for (const c of cells.values()) {
    if (c.sp < MIN_CELL_SPEND) continue;
    const vs = c.val / c.sp / baseRoas;
    if (vs < MIN_VS) continue;
    const quiet =
      globalLatestWeek !== null &&
      c.latestWeek !== null &&
      globalLatestWeek - c.latestWeek >= STALE_WEEKS;
    const reason =
      `pays ${vs.toFixed(2)}× the cold average on ${p.cur}${Math.round(c.sp).toLocaleString()}` +
      ` · ${c.produced} made` +
      (quiet ? ` · nothing new since WK${c.latestWeek}` : "");
    out.push({
      angle: c.angle,
      avatar: c.avatar,
      awareness: c.awareness,
      spend: Math.round(c.sp),
      vs,
      produced: c.produced,
      latestWeek: c.latestWeek,
      reason,
    });
  }

  out.sort(
    (a, b) =>
      (b.vs * Math.log10(Math.max(10, b.spend))) / (1 + b.produced) -
      (a.vs * Math.log10(Math.max(10, a.spend))) / (1 + a.produced),
  );
  return out.slice(0, TOP_N);
}
